/**
 * useModeValidationConges — le mode de validation des congés de la commune.
 *
 * Répond à une seule question : **cet utilisateur peut-il trancher une demande
 * de congés, ou seulement donner son avis ?**
 *
 * ─── POURQUOI UN HOOK ───────────────────────────────────────────────────────
 *
 * Deux panneaux de `RhPaiePage` valident des absences (`RhAbsencesPanel` et
 * `RhValiderPanel`). La question s'y pose à l'identique. Dupliquer le calcul
 * garantirait qu'ils divergent au premier ajustement — et un écran qui affiche
 * « Valider » alors que le serveur refuse est une impasse pour l'utilisateur.
 *
 * ─── LE PIÈGE QU'IL ÉVITE ───────────────────────────────────────────────────
 *
 * Ces écrans servent le manager **ET** la RH **ET** le maire. La réponse dépend
 * donc du mode de la commune **ET** du rôle de celui qui regarde :
 *
 *   · mode `manager`  → tout le monde peut trancher (comportement historique) ;
 *   · mode `rh_maire` → seuls RH et maire tranchent ; le manager donne son avis.
 *
 * Se tromper afficherait « Donner mon avis » à la RH — qui ne pourrait plus rien
 * valider. C'est pour ça que le calcul vit à un seul endroit.
 *
 * ⚠️ C'est une garde d'AFFICHAGE, pas de sécurité. Le serveur refuse de toute
 * façon (403 `VALIDATION_RESERVEE_RH`). Ce hook sert à ne pas montrer un bouton
 * qui échoue — il ne protège rien à lui seul.
 */

import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export interface ModeValidationConges {
  /** `null` tant que le mode n'est pas chargé. */
  mode: 'manager' | 'rh_maire' | null;
  /** Cet utilisateur peut-il valider / refuser une demande de congés ? */
  peutTrancher: boolean;
  /** Vrai quand il ne peut que donner son avis (mode rh_maire, non RH/admin). */
  avisSeulement: boolean;
}

export function useModeValidationConges(): ModeValidationConges {
  const { user } = useAuth();
  const [mode, setMode] = useState<'manager' | 'rh_maire' | null>(null);

  useEffect(() => {
    void api.rh.modeValidationConges()
      .then((r) => setMode(r.mode))
      // Best-effort : si le mode est illisible, on retombe sur `manager` —
      // le comportement historique. Bloquer l'écran pour un réglage non lu
      // serait pire que d'afficher un bouton qui pourrait échouer.
      .catch(() => setMode('manager'));
  }, []);

  // ⚠️ Le service d'un manager est la RÉUNION de `service` (son service
  // principal) et de `servicesAdditionnels` (le cumul). Ne lire que le premier
  // raterait un manager rattaché au RH en cumul : il verrait « Donner mon avis »
  // alors que le serveur l'autorise à valider.
  //
  // C'est exactement le trou corrigé côté serveur au lot N2 (résolution des
  // destinataires), et `requireRhGestion()` lit bien les deux
  // (`loadCurrentUserServices`). L'affichage doit suivre la même règle, sinon il
  // ment.
  const servicesDuUser = [
    ...(user?.service ? [user.service] : []),
    ...(user?.servicesAdditionnels ?? []),
  ];
  const estRhOuAdmin = user?.role === 'admin'
    || user?.role === 'super_admin'
    || (user?.role === 'manager' && servicesDuUser.includes('rh'));

  // Tant que le mode n'est pas chargé, on suppose qu'il peut trancher : c'est le
  // cas de la grande majorité des communes (défaut `manager`), et un bouton qui
  // apparaît vaut mieux qu'un bouton qui disparaît sous le doigt.
  const peutTrancher = mode === null || mode === 'manager' || estRhOuAdmin;

  return { mode, peutTrancher, avisSeulement: !peutTrancher };
}
